import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, FileText, Upload, Download, Loader2, CheckCircle2,
  AlertCircle, Image, FileSpreadsheet, FileImage, File, FileCode,
  Table, FileType, Cpu, Settings2,
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const FORMATS = [
  { value: "pdf", label: "PDF", icon: FileText },
  { value: "docx", label: "Word (DOCX)", icon: FileText },
  { value: "xlsx", label: "Excel (XLSX)", icon: FileSpreadsheet },
  { value: "pptx", label: "PowerPoint (PPTX)", icon: FileImage },
  { value: "png", label: "PNG Image", icon: Image },
  { value: "jpg", label: "JPEG Image", icon: FileImage },
  { value: "txt", label: "Plain Text", icon: FileCode },
  { value: "csv", label: "CSV", icon: Table },
  { value: "html", label: "HTML", icon: FileCode },
  { value: "md", label: "Markdown", icon: FileType },
  { value: "odt", label: "OpenDocument (ODT)", icon: File },
];

const ACCEPTED_EXTENSIONS = ".pdf,.docx,.xlsx,.pptx,.png,.jpg,.jpeg,.txt,.csv,.html,.htm,.md,.odt";

export default function DocConverter() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [targetFormat, setTargetFormat] = useState("pdf");
  const [enableOcr, setEnableOcr] = useState(false);
  const [ocrEngine, setOcrEngine] = useState("tesseract");
  const [ocrOutputMode, setOcrOutputMode] = useState("text_only");
  const [language, setLanguage] = useState("");
  const [converting, setConverting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState("convert"); // convert | ocr

  const fileInputRef = useRef(null);
  const ocrFileInputRef = useRef(null);

  // ── OCR only ──
  const [ocrFile, setOcrFile] = useState(null);
  const [ocrResult, setOcrResult] = useState(null);
  const [ocrConverting, setOcrConverting] = useState(false);

  const detectFormat = (filename) => {
    const ext = filename?.split(".").pop()?.toLowerCase() || "";
    const map = {
      pdf: "pdf", docx: "docx", doc: "docx", xlsx: "xlsx", xls: "xlsx",
      pptx: "pptx", ppt: "pptx", png: "png", jpg: "jpg", jpeg: "jpg",
      txt: "txt", csv: "csv", html: "html", htm: "html", md: "md", odt: "odt",
    };
    return map[ext] || "";
  };

  const handleConvert = async () => {
    if (!file) return toast.error("Select a file first");
    setConverting(true);
    setResult(null);
    setError(null);

    try {
      const form = new FormData();
      form.append("file", file);
      form.append("target_format", targetFormat);
      form.append("enable_ocr", enableOcr);
      form.append("ocr_engine", ocrEngine);
      form.append("ocr_output_mode", ocrOutputMode);
      form.append("language_code", language);

      const res = await axios.post(`${API}/api/smart-office/doc-converter/convert`, form, {
        headers: { ...getHeaders(), "Content-Type": "multipart/form-data" },
        withCredentials: true,
        responseType: "blob",
      });

      const contentDisposition = res.headers["content-disposition"] || "";
      const filenameMatch = contentDisposition.match(/filename="?(.+?)"?$/);
      const filename = filenameMatch ? filenameMatch[1] : `converted.${targetFormat}`;
      const blob = new Blob([res.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);

      setResult({ filename, size: blob.size });
      toast.success("Conversion complete!");
    } catch (err) {
      const getMsg = async () => {
        if (err.response?.data instanceof Blob) {
          try {
            const text = await err.response.data.text();
            const json = JSON.parse(text);
            return json.detail || text;
          } catch { return err.message; }
        }
        return err.response?.data?.detail || err.message;
      };
      getMsg().then(msg => { setError(msg); toast.error(msg); });
    }
    setConverting(false);
  };

  const handleOcrExtract = async () => {
    if (!ocrFile) return toast.error("Select an image or PDF file");
    setOcrConverting(true);
    setOcrResult(null);

    try {
      const form = new FormData();
      form.append("file", ocrFile);
      form.append("ocr_engine", ocrEngine);
      form.append("output_mode", ocrOutputMode);
      form.append("language_code", language);

      const res = await axios.post(`${API}/api/smart-office/doc-converter/ocr/extract`, form, {
        headers: { ...getHeaders(), "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });

      setOcrResult(res.data);
      toast.success("OCR complete!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "OCR failed");
    }
    setOcrConverting(false);
  };

  const formatSize = (bytes) => {
    if (!bytes) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  };

  const getIcon = (fmt) => {
    const f = FORMATS.find((f) => f.value === fmt);
    if (f) {
      const Icon = f.icon;
      return <Icon className="w-4 h-4 inline mr-1" />;
    }
    return <FileText className="w-4 h-4 inline mr-1" />;
  };

  return (
    <div className="min-h-screen bg-gray-950 text-gray-100">
      {/* Header */}
      <div className="border-b border-gray-800 bg-gray-900/60 backdrop-blur px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate("/smart-office")} className="text-gray-400 hover:text-white p-1">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
          <FileText className="w-4 h-4 text-white" />
        </div>
        <div>
          <h1 className="font-['JetBrains_Mono'] text-lg font-bold text-white tracking-tight">DOC CONVERTER</h1>
          <p className="text-[11px] text-gray-500">Convert documents &amp; OCR images — no storage, fully private</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 space-y-6">
        {/* Mode Toggle */}
        <div className="flex bg-gray-900 border border-gray-800 rounded-lg p-0.5 w-fit">
          <button onClick={() => setMode("convert")}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${mode === "convert" ? "bg-blue-600 text-white shadow-sm" : "text-gray-400 hover:text-white"}`}>
            <FileText className="w-3.5 h-3.5 inline mr-1.5" /> Convert
          </button>
          <button onClick={() => setMode("ocr")}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${mode === "ocr" ? "bg-blue-600 text-white shadow-sm" : "text-gray-400 hover:text-white"}`}>
            <Image className="w-3.5 h-3.5 inline mr-1.5" /> OCR Extract
          </button>
        </div>

        {mode === "convert" ? (
          /* ══════════════ CONVERT MODE ══════════════ */
          <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-5">
            <div className="flex items-center gap-2 mb-4">
              <Upload className="w-5 h-5 text-blue-400" />
              <h2 className="font-medium text-white">Convert Document</h2>
            </div>

            {/* Upload */}
            <div className="border-2 border-dashed border-gray-700 rounded-lg p-6 text-center cursor-pointer hover:border-blue-600 transition-colors mb-4"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) { setFile(f); setLanguage(detectFormat(f.name)); } }}>
              <Upload className="w-8 h-8 mx-auto mb-2 text-gray-500" />
              {file ? (
                <div>
                  <p className="text-sm text-blue-400">{file.name}</p>
                  <p className="text-xs text-gray-500">{formatSize(file.size)}</p>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-gray-400">Drop a file here or click to browse</p>
                  <p className="text-xs text-gray-600 mt-1">PDF, Word, Excel, PowerPoint, Images, TXT, CSV, HTML, Markdown, ODT</p>
                </div>
              )}
              <input ref={fileInputRef} type="file" accept={ACCEPTED_EXTENSIONS} className="hidden"
                onChange={(e) => { const f = e.target.files[0]; if (f) setFile(f); }} />
            </div>

            {/* Options */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider mb-1.5 block">Target Format</label>
                <Select value={targetFormat} onValueChange={setTargetFormat}>
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-gray-700 text-white">
                    {FORMATS.map((f) => (
                      <SelectItem key={f.value} value={f.value}>{getIcon(f.value)} {f.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider mb-1.5 block">Language (for OCR)</label>
                <input type="text" value={language} onChange={(e) => setLanguage(e.target.value)}
                  placeholder="e.g. eng, fra, zul..."
                  className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500" />
              </div>
            </div>

            {/* OCR Toggle */}
            <label className="flex items-center gap-2 mb-4 cursor-pointer">
              <input type="checkbox" checked={enableOcr} onChange={(e) => setEnableOcr(e.target.checked)}
                className="w-4 h-4 rounded border-gray-600 text-blue-600 focus:ring-blue-500 bg-gray-800" />
              <span className="text-sm text-gray-300">Enable OCR (extract text from scanned images/PDFs)</span>
            </label>

            {enableOcr && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 p-3 bg-gray-800/40 rounded-lg border border-gray-700/50">
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider mb-1.5 block">OCR Engine</label>
                  <Select value={ocrEngine} onValueChange={setOcrEngine}>
                    <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-900 border-gray-700 text-white">
                      <SelectItem value="tesseract">Tesseract 5 (General)</SelectItem>
                      <SelectItem value="easyocr">EasyOCR (Complex layouts)</SelectItem>
                      <SelectItem value="paddle">PaddleOCR (CJK languages)</SelectItem>
                      <SelectItem value="runpod_qwen">RunPod Qwen2.5-VL (Highest accuracy)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider mb-1.5 block">OCR Output</label>
                  <Select value={ocrOutputMode} onValueChange={setOcrOutputMode}>
                    <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-900 border-gray-700 text-white">
                      <SelectItem value="text_only">Text only (TXT)</SelectItem>
                      <SelectItem value="searchable_pdf">Searchable PDF</SelectItem>
                      <SelectItem value="formatted_docx">Formatted Word (DOCX)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <Button onClick={handleConvert} disabled={!file || converting}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded">
              {converting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Cpu className="w-4 h-4 mr-2" />}
              {converting ? "Converting..." : "Convert"}
            </Button>

            {result && (
              <div className="mt-4 bg-green-900/30 border border-green-700/50 rounded-lg p-3 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-400" />
                <span className="text-sm text-green-300">Converted: {result.filename} ({formatSize(result.size)})</span>
                <Button size="sm" variant="outline" className="ml-auto border-green-700 text-green-400"
                  onClick={() => setResult(null)}>Clear</Button>
              </div>
            )}
            {error && (
              <div className="mt-4 bg-red-900/30 border border-red-700/50 rounded-lg p-3 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-400" />
                <span className="text-sm text-red-300">{error}</span>
                <Button size="sm" variant="outline" className="ml-auto border-red-700 text-red-400"
                  onClick={() => setError(null)}>Clear</Button>
              </div>
            )}

            {/* Format matrix hint */}
            <div className="mt-6 p-3 bg-gray-800/30 rounded-lg border border-gray-700/30">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Supported Formats</p>
              <div className="flex flex-wrap gap-1.5">
                {FORMATS.map((f) => {
                  const Icon = f.icon;
                  return (
                    <Badge key={f.value} variant="outline" className="text-gray-400 border-gray-700 text-[10px]">
                      <Icon className="w-3 h-3 mr-0.5" /> {f.value.toUpperCase()}
                    </Badge>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          /* ══════════════ OCR EXTRACT MODE ══════════════ */
          <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-5">
            <div className="flex items-center gap-2 mb-4">
              <Image className="w-5 h-5 text-purple-400" />
              <h2 className="font-medium text-white">Extract Text from Image</h2>
              <Badge className="bg-purple-600/20 text-purple-400 border-purple-600/30">OCR</Badge>
            </div>
            <p className="text-xs text-gray-400 mb-4">Extract text from scanned documents, photos, or images.</p>

            <div className="border-2 border-dashed border-gray-700 rounded-lg p-6 text-center cursor-pointer hover:border-purple-600 transition-colors mb-4"
              onClick={() => ocrFileInputRef.current?.click()}>
              <Upload className="w-8 h-8 mx-auto mb-2 text-gray-500" />
              {ocrFile ? <p className="text-sm text-purple-400">{ocrFile.name}</p>
                : <p className="text-sm text-gray-400">Click or drop an image/PDF</p>}
              <input ref={ocrFileInputRef} type="file" accept="image/*,.pdf" className="hidden"
                onChange={(e) => ocrFile && e.target.files[0] && setOcrFile(e.target.files[0])} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider mb-1.5 block">Engine</label>
                <Select value={ocrEngine} onValueChange={setOcrEngine}>
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-gray-900 border-gray-700 text-white">
                    <SelectItem value="tesseract">Tesseract 5</SelectItem>
                    <SelectItem value="easyocr">EasyOCR</SelectItem>
                    <SelectItem value="paddle">PaddleOCR</SelectItem>
                    <SelectItem value="runpod_qwen">RunPod Qwen2.5-VL</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider mb-1.5 block">Output</label>
                <Select value={ocrOutputMode} onValueChange={setOcrOutputMode}>
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-gray-900 border-gray-700 text-white">
                    <SelectItem value="text_only">Text only</SelectItem>
                    <SelectItem value="searchable_pdf">Searchable PDF</SelectItem>
                    <SelectItem value="formatted_docx">Formatted DOCX</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider mb-1.5 block">Language</label>
                <input type="text" value={language} onChange={(e) => setLanguage(e.target.value)}
                  placeholder="eng, fra, zul..."
                  className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-purple-500" />
              </div>
            </div>

            <Button onClick={handleOcrExtract} disabled={!ocrFile || ocrConverting}
              className="w-full bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-500 hover:to-violet-500 text-white rounded">
              {ocrConverting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Image className="w-4 h-4 mr-2" />}
              {ocrConverting ? "Extracting..." : "Extract Text"}
            </Button>

            {ocrResult && (
              <div className="mt-4 space-y-3">
                {ocrResult.confidence != null && (
                  <div className="flex items-center gap-3">
                    <div className="flex-1 bg-gray-800 rounded-full h-2">
                      <div className={`h-2 rounded-full transition-all ${ocrResult.confidence >= 0.7 ? "bg-green-500" : "bg-amber-500"}`}
                        style={{ width: `${Math.min(100, ocrResult.confidence * 100)}%` }} />
                    </div>
                    <span className="text-xs font-medium text-gray-400">
                      {(ocrResult.confidence * 100).toFixed(0)}% confidence
                    </span>
                    {ocrResult.confidence < 0.7 && (
                      <Badge variant="outline" className="text-amber-400 border-amber-700">Review recommended</Badge>
                    )}
                  </div>
                )}
                <div className="bg-gray-800/60 rounded-lg p-4 max-h-[400px] overflow-y-auto">
                  <pre className="text-sm text-gray-200 whitespace-pre-wrap font-sans">{ocrResult.text}</pre>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="border-gray-700 text-gray-300"
                    onClick={() => { navigator.clipboard.writeText(ocrResult.text); toast.success("Copied!"); }}>
                    <Download className="w-3 h-3 mr-1" /> Copy Text
                  </Button>
                  <Button variant="outline" size="sm" className="border-gray-700 text-gray-300"
                    onClick={() => setOcrResult(null)}>Clear</Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
